import { AlertTriangle, CheckCircle } from '@recruit-robin/robin-theme/web-icons';
import { useContext } from 'react';

import type { Vacancy } from 'clients/AutoCompleteClient/types';
import type {
  CombinedVacancy,
  JobImprovedCreateOutput,
  JobImprovedGetDetailOutput,
} from 'clients/ReachoutClient/ReachoutClient.types';

import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';

export enum VacancyInfoLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export type VacancyInfoSufficiencyConfig = {
  [key in VacancyInfoLevel]: {
    icon: typeof AlertTriangle | typeof CheckCircle;
    title: string;
    quality: string;
    pill: {
      label: string;
      type: 'error' | 'warning' | 'success-light';
      style: 'icon-removable';
    };
    description: string;
  };
};

type VacancyWithAllProperties = {
  origin_job_title?: string | null;
  job_details?: { job_title?: string | null };
  job_title?: string | null;
  name?: string | null;
};

export const useVacancyInfoSufficiencyConfig = () => {
  const { dictionary } = useContext(LocalizationContext);

  return {
    low: {
      icon: AlertTriangle,
      title: dictionary.insufficientInfo,
      quality: dictionary.lowQuality,
      pill: {
        label: dictionary.lowReplyChance,
        type: 'error',
        style: 'icon-removable',
      },
      description: dictionary.addDetailsOrSelectAnotherJob,
    },
    medium: {
      icon: AlertTriangle,
      title: dictionary.insufficientInfo,
      quality: dictionary.mediumQuality,
      pill: {
        label: dictionary.mediumReplyChance,
        type: 'warning',
        style: 'icon-removable',
      },
      description: dictionary.addDetailsAndIncreaseYourChanceToGetAReply,
    },
    high: {
      icon: CheckCircle,
      title: dictionary.sufficientInfo,
      quality: dictionary.highQuality,
      pill: {
        label: dictionary.highReplyChance,
        type: 'success',
        style: 'icon-removable',
      },
      description: dictionary.jobDescriptionComplete,
    },
  } as const;
};

export const getVacancyName = (
  vacancy: Vacancy | CombinedVacancy | JobImprovedCreateOutput | JobImprovedGetDetailOutput,
): string => {
  const v: VacancyWithAllProperties = vacancy satisfies VacancyWithAllProperties;

  return v.origin_job_title ?? v?.job_details?.job_title ?? v.job_title ?? v.name ?? 'Untitled Vacancy';
};

export const jobImprovedToVacancy = (jobImproved: JobImprovedCreateOutput): Vacancy => {
  return {
    user_id: jobImproved.user_id,
    db_id: jobImproved.id,
    job_title: jobImproved.job_details.job_title,
    origin_job_title: jobImproved.origin_job_title,
    quality_percent: jobImproved.quality_percent,
    is_improved: true,
    job_ats_id: jobImproved.job_ats_id,
    job_indeed_id: jobImproved.job_indeed_id,
    job_campaign_id: jobImproved.job_campaign_id,
    type: jobImproved.type,
    created_at: jobImproved.created_at,
    updated_at: jobImproved.updated_at,

    name: getVacancyName(jobImproved),
    value: jobImproved.job_ats_id ?? jobImproved.job_indeed_id,
  } as Vacancy;
};

/**
 * Determines the vacancy information level based on a percentage value.
 * @param percent - The percentage value (0-100) representing the completeness of vacancy information
 * @returns The corresponding VacancyInfoLevel
 *
 * Levels are determined as follows:
 * - low: 0-33%
 * - medium: 34-66%
 * - high: 67-100%
 */
export const getVacancyInfoLevel = (percent: number): VacancyInfoLevel => {
  if (percent <= 33) return VacancyInfoLevel.LOW;
  if (percent <= 66) return VacancyInfoLevel.MEDIUM;
  return VacancyInfoLevel.HIGH;
};
